// Copyright © 2021 Move Closer

import { Component } from 'vue-property-decorator'

import { AbstractNavProps, BootstrapImage, BootstrapNav } from '../../dsl/front'

import { AbstractModuleUi, FetchesRelatedAsync } from '../_abstract/ui'
import { ImageFile, ImageRatio } from '../_contracts'
import { ModuleHeaderUi } from '../_components/ui'
import { prepareImage } from '../_filters'

import { ExpertsModule, ExpertsTab, ResolvedExpert, ResolvedExpertsTab } from './Experts.contracts'

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
@Component<ExpertsModuleUi>({
  name: 'ExpertsModuleUi',
  components: {
    BootstrapImage,
    BootstrapNav,
    ModuleHeaderUi
  },

  async created () {
    await this.fetchRelated()
  },

  template: `
    <section class="experts-module" :style="{ background }" :id="anchor">

    <!-- Header -->
    <div v-if="module.content.header" class="container">
      <div class="row">
        <div class="col">
          <ModuleHeaderUi :header="module.content.header"/>
        </div>
      </div>
    </div>
    <!-- END | Header -->

    <template v-if="tabs && tabs.length">
      <!-- Tabs / Nav -->
      <BootstrapNav :activeItem.sync="activeTab" :items="tabs"
                    class="experts-module__nav"/>

      <!-- Tabs / Content -->
      <div v-for="tab in tabs" :key="tab.id" v-show="activeTab === tab.id"
           class="experts-module__tab">
        <div class="container">
          <div class="row">
            <div class="col">

              <!-- Text -->
              <p v-if="tab.text" v-html="tab.text" class="experts-module__tab__text"/>

              <!-- Spacer -->
              <hr class="experts-module__tab__spacer">

              <!-- Experts -->
              <ul class="experts-module__tab__experts list-unstyled">
                <li v-for="expert in tab.experts" :key="expert.name"
                    class="experts-module__expert card d-flex flex-column align-items-center">
                  <!-- Name & role -->
                  <h3 class="experts-module__expert__name order-2">
                    <span v-if="expert.name" v-html="expert.name"/>
                    <small v-if="expert.role" v-html="expert.role"
                           class="experts-module__expert__role text-ui-small text-uppercase"/>
                  </h3>

                  <!-- Profile image -->
                  <BootstrapImage v-if="expert.image" v-bind="expert.image"
                                  class="experts-module__expert__image order-1"/>

                  <!-- Description -->
                  <p class="experts-module__expert__description order-3 m-0 fs-medium" v-html="expert.description"/>
                </li>
              </ul>
              <!-- END | Experts -->

            </div>
          </div>
        </div>
      </div>
      <!-- END | Tabs / Content -->
    </template>

    </section>
  `
})
export class ExpertsModuleUi extends AbstractModuleUi<ExpertsModule> implements FetchesRelatedAsync {
  /**
   * Currently-active tab.
   */
  public activeTab: AbstractNavProps['activeItem'] = ''

  /**
   * Tabs' content (resolved using `RelatedService`).
   */
  public tabs: ResolvedExpertsTab[] = []

  /**
   * @inheritDoc
   */
  protected imageRatios = {
    desktop: ImageRatio.Square,
    mobile: ImageRatio.Square
  }

  public get anchor (): string | null {
    if (this.module.content.anchor) {
      return this.module.content.anchor
    }
    if (this.module.id === null) return null
    return this.module.id
  }

  /**
   * @inheritDoc
   */
  public async fetchRelated (): Promise<void> {
    try {
      await this.composeTabs()
    } catch (e) {
      this.onError(e)
    }

    if (this.tabs.length > 0) {
      this.activeTab = this.tabs[0].id
      this.onReady()
    } else {
      this.onError()
    }
  }

  /**
   * Composes the tabs array.
   */
  private async composeTabs (): Promise<void> {
    const tabsToResolve = Array.isArray(this.module.content.tabs) ? this.module.content.tabs : []

    await Promise.all(tabsToResolve.map((rawTab, index) => {
      return new Promise((resolve) => {
        this.resolveSingleTab(rawTab, index)
          .catch(e => {
            if (this.debug) console.warn(e)
          })
          .finally(() => {
            this.tabs = this.tabs.filter(tab => !!tab)
            resolve()
          })
      })
    }))
  }

  /**
   * Resolves all related data for the passed-in tab.
   *
   * @param rawTab - The tab which content is to be resolved.
   * @param index - Tab's index in `this.tabs` array.
   */
  private async resolveSingleTab (rawTab: ExpertsTab, index: number): Promise<void> {
    const {
      experts,
      label,
      text
    } = rawTab

    const resolvedExperts: ResolvedExpert[] = []

    await Promise.all(experts.map(expert => {
      return new Promise((resolve, reject) => {
        if (expert.image === null) {
          resolvedExperts.push(expert as ResolvedExpert)
          return resolve()
        }
        this.relatedService.resolve(expert.image)
          .then(resolvedImage => {
            resolvedExperts.push({
              ...expert,
              image: prepareImage(resolvedImage as ImageFile, this.imageRatio, 500, this.module.isLazyLoaded)
            })
            return resolve()
          })
          .catch(e => {
            if (this.debug) console.warn(e)
            return reject(e)
          })
      })
    }))

    const tab: ResolvedExpertsTab = {
      experts: resolvedExperts,
      id: `${label}-${index}`,
      label,
      text
    }

    const tabCopy = [...this.tabs]
    tabCopy[index] = tab

    this.tabs = tabCopy
  }
}
